import type { Doc } from "building-types/doc";
import type {
  HorizontalEdgeParamMatchExpr,
  VerticalEdgePlacementMatchExpr,
} from "building-types/entities/edgePlacementTypes";
import type { MatchExpression } from "building-types/options/matchExpressionTypes";
import { isMatchExpression } from "building-types/options/matchExpressionTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import { resolveOptionsInFormula } from "../../formula/evalFormulas";
import {
  getMatchExpressionActiveValue,
  matchExpressionActiveValue,
  matchExpressionFormulasEqual,
  setMatchExpressionActiveValue,
} from "../../formula/matchExpressionTools";

export function resolveNumberFromMatchExpression(
  verticalOffset: number | MatchExpression<number>,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): number {
  if (isMatchExpression(verticalOffset)) {
    const activeValue = matchExpressionActiveValue(
      verticalOffset,
      resolveOptionsInFormula(verticalOffset, definitions, optionChoices),
    );
    return getMatchExpressionActiveValue(verticalOffset, activeValue);
  }
  return verticalOffset;
}

export function setNumberInMatchExpressionIfRequired(
  optionsSetsAndChoices: Pick<Doc, "optionSets" | "instanceOptionSets" | "optionChoices">,
  verticalOffsetMatchExpression: MatchExpression<number> | number | undefined,
  verticalOffset: number | undefined,
): number | MatchExpression<number> | undefined {
  if (isMatchExpression(verticalOffsetMatchExpression)) {
    const activeValue = matchExpressionActiveValue(
      verticalOffsetMatchExpression,
      resolveOptionsInFormula(
        verticalOffsetMatchExpression,
        optionsSetsAndChoices,
        optionsSetsAndChoices.optionChoices,
      ),
    );
    return setMatchExpressionActiveValue(verticalOffsetMatchExpression, activeValue, verticalOffset ?? 0);
  }
  return verticalOffset;
}

export function resolvePlacementFromMatchExpression<
  T extends HorizontalEdgeParamMatchExpr & VerticalEdgePlacementMatchExpr,
>(
  placement: T,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): T & { param: number; paramNormalized: boolean; verticalOffset: number } {
  const newPlacement = resolveHorizontalPlacementFromMatchExpression(placement, definitions, optionChoices);
  const activeVerticalOffset = resolveNumberFromMatchExpression(
    newPlacement.verticalOffset,
    definitions,
    optionChoices,
  );

  return {
    ...newPlacement,
    verticalOffset: activeVerticalOffset,
  };
}

export function resolveHorizontalPlacementFromMatchExpression<T extends HorizontalEdgeParamMatchExpr>(
  placement: T,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): T & { param: number; paramNormalized: boolean } {
  let resolvedParam: number | undefined;
  let resolvedParamNormalized: boolean | undefined;

  const paramIsMatchExpression = isMatchExpression(placement.param);
  const paramNormalizedIsMatchExpression = isMatchExpression(placement.paramNormalized);

  if (paramIsMatchExpression !== paramNormalizedIsMatchExpression) {
    throw new Error(
      "Both param and paramNormalized must either both be MatchExpressions or both be primitives. They can't be mixed.",
    );
  }

  if (paramIsMatchExpression && paramNormalizedIsMatchExpression) {
    if (!matchExpressionFormulasEqual(placement.param, placement.paramNormalized)) {
      throw new Error(
        "Both param and paramNormalized must either both be MatchExpressions or both be primitives. They can't be mixed.",
      );
    }

    const activeValue = matchExpressionActiveValue(
      placement.param,
      resolveOptionsInFormula(placement, definitions, optionChoices),
    );
    resolvedParam = getMatchExpressionActiveValue(placement.param, activeValue);
    resolvedParamNormalized = getMatchExpressionActiveValue(placement.paramNormalized, activeValue);
  } else {
    resolvedParam = placement.param as number;
    resolvedParamNormalized = placement.paramNormalized as boolean;
  }

  return {
    ...placement,
    param: resolvedParam,
    paramNormalized: resolvedParamNormalized,
  };
}

export function setParamInMatchExpressionIfRequired(
  optionsSetsAndChoices: Pick<Doc, "optionSets" | "instanceOptionSets" | "optionChoices">,
  edgeParam: HorizontalEdgeParamMatchExpr,
  param: number,
) {
  if (placementContainsMatchExpression(edgeParam)) {
    const activeValue = matchExpressionActiveValue(
      edgeParam.param,
      resolveOptionsInFormula(edgeParam, optionsSetsAndChoices, optionsSetsAndChoices.optionChoices),
    );
    return setMatchExpressionActiveValue(edgeParam.param, activeValue, param);
  }
  return param;
}

export function setParamNormalizedInMatchExpressionIfRequired(
  optionsSetsAndChoices: Pick<Doc, "optionSets" | "instanceOptionSets" | "optionChoices">,
  edgeParam: HorizontalEdgeParamMatchExpr,
  paramNormalized: boolean,
) {
  if (placementContainsMatchExpression(edgeParam)) {
    const activeValue = matchExpressionActiveValue(
      edgeParam.paramNormalized,
      resolveOptionsInFormula(edgeParam, optionsSetsAndChoices, optionsSetsAndChoices.optionChoices),
    );
    return setMatchExpressionActiveValue(edgeParam.paramNormalized, activeValue, paramNormalized);
  }
  return paramNormalized;
}

function placementContainsMatchExpression(
  placement: HorizontalEdgeParamMatchExpr,
): placement is HorizontalEdgeParamMatchExpr & {
  param: MatchExpression<number>;
  paramNormalized: MatchExpression<boolean>;
} {
  return isMatchExpression(placement.param) && isMatchExpression(placement.paramNormalized);
}
